import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Star } from 'lucide-react';
import type { PlanId } from '../pages/SalesPage';
function Countdown() {
  const [timeLeft, setTimeLeft] = useState(23 * 3600 + 47 * 60 + 30);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const h = Math.floor(timeLeft / 3600);
  const m = Math.floor(timeLeft % 3600 / 60);
  const s = timeLeft % 60;
  return <div className="inline-flex items-center gap-1 md:gap-2 bg-red-100 text-red-800 px-3 py-1.5 md:px-4 md:py-2 rounded-full font-mono font-bold text-xs md:text-base mb-4 md:mb-6" data-id="element-162">
      <span data-id="element-163">⏳ TERMINA EN:</span>
      <span data-id="element-164">{h.toString().padStart(2, '0')} Hs</span>
      <span data-id="element-165">:</span>
      <span data-id="element-166">{m.toString().padStart(2, '0')} Min</span>
      <span data-id="element-167">:</span>
      <span data-id="element-168">{s.toString().padStart(2, '0')} Seg</span>
    </div>;
}
interface PlanCardProps {
  id: PlanId;
  selected: boolean;
  onSelect: (id: PlanId) => void;
  title: string;
  oldPrice: string;
  price: string;
  features: string[];
  highlight?: boolean;
}
function PlanCard({
  id,
  selected,
  onSelect,
  title,
  oldPrice,
  price,
  features,
  highlight
}: PlanCardProps) {
  const dark = highlight;
  return <motion.button type="button" role="radio" aria-checked={selected} onClick={() => onSelect(id)} whileTap={{
    scale: 0.99
  }} className={`relative w-full text-left rounded-2xl p-4 md:p-6 border-2 transition-all focus:outline-none focus:ring-2 focus:ring-gold-500/60 ${dark ? 'bg-navy-950 text-cream-50' : 'bg-white text-navy-900'} ${selected ? 'border-gold-500 shadow-[0_10px_30px_-8px_rgba(200,162,74,0.45)] scale-[1.01]' : 'border-cream-200 opacity-80 hover:opacity-100 hover:border-gold-500/40'}`} data-id="element-169">
      {highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold-500 text-navy-950 font-bold px-3 py-0.5 rounded-full text-[10px] md:text-xs flex items-center gap-1 whitespace-nowrap shadow" data-id="element-170">
          <Star className="w-3 h-3 fill-navy-950" data-id="element-171" /> MÁS ELEGIDO — AHORRÁS
          $10.000
        </div>}

      {/* Radio indicator */}
      <div className="flex items-start gap-3 mb-3 mt-1" data-id="element-172">
        <span className={`mt-0.5 shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${selected ? 'border-gold-500 bg-gold-500' : dark ? 'border-cream-50/40' : 'border-navy-900/30'}`} data-id="element-173">
          {selected && <Check className="w-3 h-3 text-navy-950" strokeWidth={3} data-id="element-174" />}
        </span>
        <div className="flex-1" data-id="element-175">
          <h4 className={`text-sm md:text-lg font-bold leading-tight ${dark ? 'text-cream-50' : 'text-navy-900'}`} data-id="element-176">
            {title}
          </h4>
          <div className="flex items-baseline gap-2 mt-1" data-id="element-177">
            <span className={`text-sm md:text-base line-through font-medium ${dark ? 'text-cream-50/40' : 'text-navy-900/40'}`} data-id="element-178">
              {oldPrice}
            </span>
            <span className={`text-2xl md:text-3xl font-bold ${dark ? 'text-gold-500' : 'text-navy-900'}`} data-id="element-179">
              {price}
            </span>
          </div>
        </div>
      </div>

      <ul className="space-y-1 md:space-y-1.5 pl-8" data-id="element-180">
        {features.map((item, i) => <li key={i} className="flex items-start gap-2" data-id="element-181">
            <Check className="w-3.5 h-3.5 md:w-4 md:h-4 text-gold-500 shrink-0 mt-0.5" data-id="element-182" />
            <span className={`text-xs md:text-sm leading-snug ${dark ? 'text-cream-50/90' : 'text-navy-900/80'}`} data-id="element-183">
              {item}
            </span>
          </li>)}
      </ul>
    </motion.button>;
}
interface PricingProps {
  selectedPlan: PlanId;
  onSelectPlan: (id: PlanId) => void;
  onBuy: () => void;
}
export function Pricing({
  selectedPlan,
  onSelectPlan,
  onBuy
}: PricingProps) {
  return <section id="pricing" className="py-10 md:py-20 px-4 md:px-6 relative" data-id="element-184">
      <div className="max-w-4xl mx-auto" data-id="element-185">
        <div className="text-center mb-6 md:mb-8" data-id="element-186">
          <h2 className="text-xs md:text-sm font-bold uppercase tracking-widest text-red-600 mb-2 md:mb-3" data-id="element-187">
            Precio de lanzamiento — sube en breve
          </h2>
          <Countdown data-id="element-188" />
          <h3 className="text-2xl md:text-4xl font-serif font-bold text-navy-900 mb-1 md:mb-2" data-id="element-189">
            Elegí tu opción
          </h3>
          <p className="text-sm md:text-lg text-navy-900/70" data-id="element-190">
            Empezá a preparar encuentros en 10 minutos
          </p>
        </div>

        <div role="radiogroup" aria-label="Opciones de compra" className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5 items-stretch mb-5 md:mb-6 mt-4" data-id="element-191">
          <PlanCard id="standard" selected={selectedPlan === 'standard'} onSelect={onSelectPlan} title="MEGAKIT DE CATEQUESIS INFANTIL" oldPrice="$34.900" price="$19.900" features={['2 Tomos de actividades', 'Los 5 bonos estratégicos incluidos', 'PDF imprimible · Acceso inmediato']} data-id="element-192" />
          <PlanCard id="pro" selected={selectedPlan === 'pro'} onSelect={onSelectPlan} title="MEGAKIT DE CATEQUESIS INFANTIL PRO" oldPrice="$43.900" price="$29.900" highlight features={['Todo el MegaKit + los 5 bonos', '⭐ Tomo Exclusivo: 10 Planificaciones Completas', 'Versículo + dinámica + actividad + oración']} data-id="element-193" />
        </div>

        {/* Shared purchase button */}
        <div className="max-w-md mx-auto text-center" data-id="element-194">
          <motion.button whileHover={{
          y: -2,
          boxShadow: '0 8px 24px rgba(200,162,74,0.35)'
        }} whileTap={{
          scale: 0.98
        }} onClick={onBuy} className="w-full bg-gold-500 hover:bg-gold-600 text-navy-950 font-bold py-3.5 md:py-4 px-6 rounded-full text-base md:text-lg shadow-[0_4px_14px_0_rgba(200,162,74,0.39)] transition-colors" data-id="element-195">
            💛 QUIERO MI MEGAKIT AHORA
          </motion.button>
          <p className="text-center text-[11px] md:text-xs text-navy-900/60 font-medium mt-3" data-id="element-196">
            Pago seguro · Acceso inmediato · Garantía 7 días
          </p>
        </div>
      </div>
    </section>;
}